import React, {
  useCallback,
  useState,
} from 'react';
import {
  message,
  Select,
  Spin,
} from 'antd';
import {
  arrayOf,
  number,
  shape,
  string,
} from 'prop-types';
import axios from 'axios';
import I18n from 'i18n-js';
import { switch_company_memberships_path } from 'js-routes/generated/routes';
import { buildStorageKeyForCompany } from 'helpers/buildStorageKeyFor';
import {
  LAST_VISITED_PATH_KEY,
  recordLastVisitedPathFor,
} from 'helpers/recordLastVisitedPath';

const FALLBACK_PATH = '/';
const HEAD_PROBE_TIMEOUT_MS = 3000;

const isSafeInternalPath = (path) => {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) {
    return false;
  }
  try {
    const url = new URL(path, globalThis.location.origin);
    return url.origin === globalThis.location.origin;
  } catch {
    return false;
  }
};

const readStoredPath = (targetCompanyId) => {
  try {
    return localStorage.getItem(
      buildStorageKeyForCompany(targetCompanyId, LAST_VISITED_PATH_KEY),
    );
  } catch {
    return null;
  }
};

const resolveRedirectPath = async (targetCompanyId) => {
  const storedPath = readStoredPath(targetCompanyId);
  if (!storedPath || storedPath === FALLBACK_PATH) return FALLBACK_PATH;
  if (!isSafeInternalPath(storedPath)) return FALLBACK_PATH;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), HEAD_PROBE_TIMEOUT_MS);
  try {
    const response = await fetch(storedPath, {
      method: 'HEAD',
      redirect: 'manual',
      signal: controller.signal,
    });
    return response.ok || response.type === 'opaqueredirect' ? storedPath : FALLBACK_PATH;
  } catch {
    return FALLBACK_PATH;
  } finally {
    clearTimeout(timeoutId);
  }
};

const CompanySelector = ({ item }) => {
  const { memberships, currentCompanyId } = item;
  const [spinning, setSpinning] = useState(false);

  const changeCompany = useCallback(async (companyId) => {
    setSpinning(true);
    try {
      recordLastVisitedPathFor(currentCompanyId);
      const { data } = await axios.post(
        switch_company_memberships_path({ format: 'json' }),
        { companyId },
      );
      const resolvedCompanyId = data?.resolved_company_id ?? companyId;
      globalThis.location.href = await resolveRedirectPath(resolvedCompanyId);
    } catch {
      setSpinning(false);
      message.error(I18n.t('components.company_selector.messages.switch_failed'));
    }
  }, [currentCompanyId]);

  return (
    <>
      <Spin spinning={spinning} fullscreen />
      <Select
        showSearch
        optionFilterProp="label"
        defaultValue={currentCompanyId}
        getPopupContainer={() => document.body}
        options={memberships}
        onChange={changeCompany}
      />
    </>
  );
};

CompanySelector.propTypes = {
  item: shape({
    memberships: arrayOf(shape({
      key: string.isRequired,
      label: string.isRequired,
      value: number.isRequired,
    })).isRequired,
    currentCompanyId: number.isRequired,
  }).isRequired,
};

export default CompanySelector;
